<template>
	<section class="cooperative-media" :aria-label="t(`${I18N_NS}.cooperativeMedia.ariaLabel`)">
		<div class="cooperative-media__container pc-center-1200">
			<header class="cooperative-media__header">
				<div class="cooperative-media__title-row">
					<img
						class="cooperative-media__decor"
						src="../../images/title-decor-left.png"
						alt=""
						aria-hidden="true"
					/>
					<h2 class="cooperative-media__title">{{ t(`${I18N_NS}.cooperativeMedia.title`) }}</h2>
					<img
						class="cooperative-media__decor"
						src="../../images/title-decor-right.png"
						alt=""
						aria-hidden="true"
					/>
				</div>
				<p class="cooperative-media__subtitle">{{ t(`${I18N_NS}.cooperativeMedia.subtitle`) }}</p>
			</header>

			<div class="cooperative-media__group-list">
				<section
					v-for="(group, index) in logoGroups"
					:key="`${group.title}-${index}`"
					class="cooperative-media__group"
				>
					<div class="cooperative-media__group-info">
						<img
							class="cooperative-media__group-icon"
							:src="group.icon"
							alt=""
							aria-hidden="true"
						/>
						<div class="cooperative-media__group-text">
							<h3 class="cooperative-media__group-title">{{ group.title }}</h3>
							<p class="cooperative-media__group-desc">{{ group.subtitle }}</p>
						</div>
					</div>

					<div class="cooperative-media__logo-list">
						<div
							v-for="logo in group.logos"
							:key="logo.key"
							:ref="el => setLogoRef(el, logo)"
							class="cooperative-media__logo-item"
						>
							<img :src="logo.src" :alt="logo.alt" class="cooperative-media__logo-image" />
						</div>
					</div>
				</section>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted } from "vue";
import iconTv from "./images/tv.png";
import iconPlatform from "./images/platform.png";
import iconChannel from "./images/channel.png";
import iconMedia from "./images/media.png";
import { useI18n } from "~/composables/useI18n";
import { useAboutUsTracking } from "../../../composables/useAboutUsTracking";

const I18N_NS = "aboutUs";
const { t, trackTranslationDeps } = useI18n();
const { track } = useAboutUsTracking("pc");

const logoRefs = new Map();
const impressedLogoKeys = new Set();
const impressionTimers = new Map();
let logoObserver = null;

const GROUP_ICONS = [iconTv, iconPlatform, iconChannel, iconMedia];
const GROUP_LOGO_CONFIG = [
	{ prefix: "tv", count: 5 },
	{ prefix: "platform", count: 2 },
	{ prefix: "channel", count: 5 },
	{ prefix: "media", count: 19, excludedIndexes: new Set([1, 2, 3, 4, 5, 8, 14, 15, 19]) },
];
const PARTNER_NAME_MAP = {
	tv1: "Astro",
	tv2: "TVB",
	tv3: "Singtel",
	tv4: "Metro 新城",
	tv5: "ViuTV",
	platform1: "myTV SUPER",
	platform2: "WeTV",
	channel1: "YouTube",
	channel2: "Facebook",
	channel3: "Instagram",
	channel4: "WhatsApp",
	channel5: "TikTok",
	media6: "光华日报",
	media7: "生活",
	media9: "NEWSBEE",
	media10: "卓越雜誌",
	media11: "Melody",
	media12: "中國報",
	media13: "星洲日報",
	media16: "南洋商報",
	media17: "東方日報",
	media18: "Xuan",
};

const logoModules = import.meta.glob("../../../images/*.{png,jpg,jpeg,webp,svg}", {
	eager: true,
	import: "default",
});

const getLogoSrc = name => {
	const filePath = Object.keys(logoModules).find(key => key.endsWith(`/images/${name}.png`));
	return filePath ? logoModules[filePath] : "";
};

const createLogoList = (prefix, count, excludedIndexes = new Set()) =>
	Array.from({ length: count }, (_, index) => {
		if (excludedIndexes.has(index + 1)) {
			return null;
		}

		const name = `${prefix}${index + 1}`;
		const partnerName = PARTNER_NAME_MAP[name] || name;
		return {
			key: name,
			src: getLogoSrc(name),
			alt: partnerName,
			partnerName,
		};
	}).filter(item => item?.src);

const logoGroups = computed(() => {
	trackTranslationDeps();
	return GROUP_LOGO_CONFIG.map((config, index) => ({
		title: t(`${I18N_NS}.cooperativeMedia.groups.${index}.title`),
		subtitle: t(`${I18N_NS}.cooperativeMedia.groups.${index}.subtitle`),
		icon: GROUP_ICONS[index],
		logos: createLogoList(config.prefix, config.count, config.excludedIndexes || new Set()),
	}));
});

const flatLogoList = computed(() => {
	const items = [];
	logoGroups.value.forEach(group => {
		group.logos.forEach(logo => {
			items.push({
				...logo,
				partner_type: group.title,
			});
		});
	});
	return items.map((logo, index) => ({
		...logo,
		position: index + 1,
	}));
});

const setLogoRef = (el, logo) => {
	if (!logo?.key) return;
	if (el) {
		logoRefs.set(logo.key, el);
	} else {
		logoRefs.delete(logo.key);
	}
};

function refreshLogoObserver() {
	if (!import.meta.client) return;
	logoObserver?.disconnect();

	logoObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const key = entry.target.dataset.logoKey;
				if (!key || impressedLogoKeys.has(key)) return;

				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(key)) return;
					const timer = window.setTimeout(() => {
						const logo = flatLogoList.value.find(item => item.key === key);
						if (!logo || impressedLogoKeys.has(key)) return;
						impressedLogoKeys.add(key);
						track("media_partner_logo_impression", {
							partner_type: logo.partner_type,
							partner_name: logo.partnerName,
							position: logo.position,
						});
					}, 1000);
					impressionTimers.set(key, timer);
				} else if (impressionTimers.has(key)) {
					window.clearTimeout(impressionTimers.get(key));
					impressionTimers.delete(key);
				}
			});
		},
		{ threshold: [0.5] }
	);

	flatLogoList.value.forEach(logo => {
		const el = logoRefs.get(logo.key);
		if (!el) return;
		el.dataset.logoKey = logo.key;
		logoObserver.observe(el);
	});
}

onMounted(() => {
	nextTick(refreshLogoObserver);
});

onBeforeUnmount(() => {
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
	logoObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.cooperative-media {
	background: #ffffff;
	margin: 64px 0 80px;

	&__header {
		margin-bottom: 40px;
		text-align: center;
	}

	&__title-row {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 16px;
		margin-bottom: 12px;
	}

	&__decor {
		width: 120px;
		height: 16px;
		object-fit: contain;
		flex-shrink: 0;
	}

	&__title {
		margin: 0;
		color: #333333;
		font-size: 42px;
		font-weight: 700;
		line-height: 60px;
		white-space: nowrap;
	}

	&__subtitle {
		margin: 0;
		color: #333333;
		font-size: 32px;
		line-height: 46px;
	}

	&__group-list {
		display: flex;
		flex-direction: column;
		gap: 20px;
	}

	&__group {
		display: flex;
		align-items: center;
		gap: 32px;
		padding: 28px 28px 28px 40px;
		background: #ffffff;
		border: 1px solid #efefef;
		border-radius: 16px;
	}

	&__group-info {
		display: flex;
		align-items: center;
		gap: 16px;
		flex-shrink: 0;
		width: 280px;
	}

	&__group-icon {
		width: 56px;
		height: 56px;
		flex-shrink: 0;
		object-fit: contain;
	}

	&__group-text {
		min-width: 0;
	}

	&__group-title {
		margin: 0 0 6px;
		color: #333333;
		font-size: 24px;
		font-weight: bold;
		line-height: 1.4;
	}

	&__group-desc {
		margin: 0;
		color: #a0a0a0;
		font-size: 18px;
		line-height: 1.5;
	}

	&__logo-list {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		align-content: flex-start;
		flex: 1;
		min-width: 0;
	}

	&__logo-item {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 168px;
		height: 93px;
		box-sizing: border-box;
		background: #ffffff;
		border-radius: 8px;
	}

	&__logo-image {
		display: block;
		max-width: 100%;
		max-height: 100%;
		object-fit: contain;
	}
}

@media (max-width: 1024px) {
	.cooperative-media {
		margin: 48px 0 64px;

		&__title {
			font-size: 36px;
			line-height: 1.4;
		}

		&__subtitle {
			font-size: 26px;
			line-height: 1.4;
		}

		&__group {
			flex-direction: column;
			align-items: stretch;
			gap: 20px;
			padding: 24px;
		}

		&__group-info {
			width: 100%;
		}

		&__logo-item {
			width: calc(25% - 9px);
			min-width: 120px;
		}
	}
}

@media (max-width: 758px) {
	.cooperative-media {
		margin: 40px 0 48px;

		&__decor {
			width: clamp(48px, 16vw, 80px);
			height: 12px;
		}

		&__title-row {
			gap: 10px;
		}

		&__title {
			white-space: normal;
			font-size: 28px;
		}

		&__subtitle {
			font-size: 20px;
		}

		&__group {
			padding: 20px 16px;
			border-radius: 12px;
		}

		&__group-title {
			font-size: 20px;
		}

		&__group-desc {
			font-size: 16px;
		}

		&__logo-item {
			width: calc((100% - 24px) / 3);
			min-width: 0;
			height: 80px;
		}
	}
}

@media (max-width: 480px) {
	.cooperative-media {
		&__logo-item {
			width: calc((100% - 12px) / 2);
			height: 72px;
		}
	}
}
</style>
